import React, { useState } from "react";
import { toast } from "sonner";
import { CreditCard, Lock, ExternalLink } from "lucide-react";
import AppShell from "../components/AppShell";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Settings() {
  const { user, logout } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [paying, setPaying] = useState(null); // 'monthly' | 'quarterly' | null

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error("Nueva contraseña: mínimo 8 caracteres"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Contraseña actualizada. Vuelve a entrar.");
      setTimeout(logout, 1200);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSavingPw(false); }
  };

  const subscribe = async (plan) => {
    setPaying(plan);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", { origin_url: origin, plan });
      window.location.href = data.url;
    } catch (err) { toast.error(formatErr(err)); setPaying(null); }
  };

  const renews = user?.subscription_renews_at ? new Date(user.subscription_renews_at) : null;
  const subActive = user?.subscription_status === "active";

  return (
    <AppShell>
      <div className="qv-page-head" data-testid="settings-page">
        <div className="label">Ajustes</div>
        <h1>Tu cuenta</h1>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        {/* Account info */}
        <div className="qv-card-app">
          <div className="qv-card-head"><h3>Cuenta</h3></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
            <div><span style={{ color: "#737373", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase" }}>Nombre</span><div>{user?.name}</div></div>
            <div><span style={{ color: "#737373", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase" }}>Email</span><div>{user?.email}</div></div>
            <div><span style={{ color: "#737373", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase" }}>Rol</span><div>{user?.role === "admin" ? "Entrenador" : "Jugador"}</div></div>
          </div>
        </div>

        {/* Subscription (only for clients) */}
        {user?.role === "client" && (
          <div className="qv-card-app" style={{ gridColumn: "1/-1" }}>
            <div className="qv-card-head">
              <h3>Suscripción</h3>
              <span className={`qv-pill ${user?.subscription_status || "trial"}`}>{user?.subscription_status || "trial"}</span>
            </div>
            <div style={{ fontSize: 12, color: "#a3a3a3", marginBottom: 16 }}>
              Elige tu plan para esta off-season. {subActive && renews && <>Renueva el <b style={{ color: "#fff" }}>{renews.toLocaleDateString("es-ES")}</b>.</>}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
              {/* Monthly */}
              <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.08)", padding: 16, display: "flex", flexDirection: "column", gap: 10 }} data-testid="plan-monthly">
                <div className="qv-overline">Plan Mensual</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontFamily: "Bebas Neue", fontSize: "3rem", color: "var(--qv-red)", lineHeight: 1 }}>60</span>
                  <span style={{ color: "#a3a3a3", fontSize: 14 }}>€/mes</span>
                </div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12, color: "#cfcfcf", display: "flex", flexDirection: "column", gap: 4 }}>
                  <li>· Acceso completo a la plataforma</li>
                  <li>· Renovación cada 30 días</li>
                  <li>· Cancela cuando quieras</li>
                </ul>
                <button onClick={() => subscribe("monthly")} disabled={paying !== null} className="qv-btn-primary" style={{ marginTop: 4 }} data-testid="subscribe-monthly">
                  <CreditCard size={14} /> {paying === "monthly" ? "REDIRIGIENDO..." : "ELEGIR MENSUAL"} <ExternalLink size={12} />
                </button>
              </div>

              {/* Quarterly */}
              <div style={{ background: "linear-gradient(135deg, rgba(225,29,46,0.10) 0%, #0a0a0a 100%)", border: "1px solid rgba(225,29,46,0.5)", padding: 16, display: "flex", flexDirection: "column", gap: 10, position: "relative" }} data-testid="plan-quarterly">
                <span style={{ position: "absolute", top: -10, right: 12, background: "var(--qv-red)", color: "#fff", fontSize: 9, padding: "3px 8px", letterSpacing: "0.22em", fontWeight: 700 }}>AHORRA 30€</span>
                <div className="qv-overline" style={{ color: "var(--qv-red)" }}>Plan Trimestral</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontFamily: "Bebas Neue", fontSize: "3rem", color: "var(--qv-red)", lineHeight: 1 }}>150</span>
                  <span style={{ color: "#a3a3a3", fontSize: 14 }}>€ / 3 meses</span>
                </div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12, color: "#cfcfcf", display: "flex", flexDirection: "column", gap: 4 }}>
                  <li>· 50€/mes (vs 60€ mensual)</li>
                  <li>· 90 días de acceso completo</li>
                  <li>· Compromiso recomendado para off-season</li>
                </ul>
                <button onClick={() => subscribe("quarterly")} disabled={paying !== null} className="qv-btn-primary" style={{ marginTop: 4 }} data-testid="subscribe-quarterly">
                  <CreditCard size={14} /> {paying === "quarterly" ? "REDIRIGIENDO..." : "ELEGIR TRIMESTRAL"} <ExternalLink size={12} />
                </button>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#737373", marginTop: 14 }}>Pago seguro vía Stripe.</div>
          </div>
        )}

        {/* Change password */}
        <form onSubmit={changePw} className="qv-card-app" style={{ gridColumn: "1/-1" }}>
          <div className="qv-card-head"><h3>Cambiar contraseña</h3></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="qv-field"><label>Actual</label><input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required data-testid="pw-current" /></div>
            <div className="qv-field"><label>Nueva (8+ caracteres)</label><input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required minLength={8} data-testid="pw-new" /></div>
          </div>
          <button type="submit" disabled={savingPw} className="qv-btn-primary" style={{ marginTop: 16 }} data-testid="pw-submit">
            <Lock size={14} /> {savingPw ? "GUARDANDO..." : "CAMBIAR CONTRASEÑA"}
          </button>
        </form>
      </div>
    </AppShell>
  );
}
